import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { DocumentNode } from 'graphql';
import { Observable, from, throwError } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { ApolloQueryResult, ApolloError } from '@apollo/client/core';

@Injectable({
  providedIn: 'root'
})
export class GraphQLService {
  constructor(
    private apollo: Apollo
  ) {}

  /**
   * Execute a GraphQL query
   * @param query GraphQL query document
   * @param variables Query variables
   * @returns Observable of query result
   */
  public query<T = any>(query: DocumentNode, variables?: any): Observable<T> {
    return this.apollo.query<any>({
      query,
      variables,
      fetchPolicy: 'network-only'
    }).pipe(
      map((result: ApolloQueryResult<any>) => {
        // Check for GraphQL errors in the response
        if (result.errors && result.errors.length > 0) {
          // Extract error message and details
          const errorMessage = result.errors[0].message;
          const errorDetails = result.errors[0].extensions?.classification || 'GRAPHQL_ERROR';
          
          // Create a custom error object with GraphQL error details
          const error: any = {
            message: errorMessage,
            graphQLErrors: result.errors,
            errorType: errorDetails
          };
          
          // Throw the error to be caught by catchError
          throw error;
        }
        
        return result.data;
      }),
      catchError(error => {
        // If error already has our custom format (thrown from map), pass it through
        if (error.message && error.graphQLErrors) {
          let errorMessage = error.graphQLErrors?.length > 0 
            ? error.graphQLErrors[0].message 
            : error.message;
          
          // Replace generic error message with user-friendly one
          if (errorMessage === 'Error message not found.') {
            errorMessage = 'Something went wrong. Please try again.';
          }
          
          return throwError({
            message: errorMessage,
            graphQLErrors: error.graphQLErrors,
            errorType: error.graphQLErrors?.[0]?.extensions?.classification || 'NETWORK_ERROR'
          });
        }
        
        // Handle ApolloError or network errors
        if (error instanceof ApolloError) {
          let errorMessage = error.graphQLErrors?.length > 0 
            ? error.graphQLErrors[0].message 
            : error.message;
          
          // Replace generic error message with user-friendly one
          if (errorMessage === 'Error message not found.') {
            errorMessage = 'Something went wrong. Please try again.';
          }
          
          const errorObj = {
            message: errorMessage,
            graphQLErrors: error.graphQLErrors,
            networkError: error.networkError,
            errorType: error.graphQLErrors?.[0]?.extensions?.classification || 'NETWORK_ERROR'
          };
          return throwError(errorObj);
        }
        
        return throwError(error);
      })
    );
  }

  /**
   * Execute a GraphQL mutation
   * @param mutation GraphQL mutation document
   * @param variables Mutation variables
   * @returns Observable of mutation result
   */
  public mutate<T = any>(mutation: DocumentNode, variables?: any): Observable<T> {
    return this.apollo.mutate<any>({
      mutation,
      variables
    }).pipe(
      map(result => {
        // Check for GraphQL errors in the response
        if (result.errors && result.errors.length > 0) {
          // Extract error message and details
          const errorMessage = result.errors[0].message;
          const errorDetails = result.errors[0].extensions?.classification || 'GRAPHQL_ERROR';
          
          // Create a custom error object with GraphQL error details
          const error: any = {
            message: errorMessage,
            graphQLErrors: result.errors,
            errorType: errorDetails
          };
          
          // Throw the error to be caught by catchError
          throw error;
        }
        
        return result.data;
      }),
      catchError(error => {
        // If error already has our custom format (thrown from map), pass it through
        if (error.message && error.graphQLErrors) {
          return throwError(error);
        }
        
        // Handle ApolloError or network errors
        if (error instanceof ApolloError) {
          let errorMessage = error.graphQLErrors?.length > 0 
            ? error.graphQLErrors[0].message 
            : error.message;
          
          // Replace generic error message with user-friendly one
          if (errorMessage === 'Error message not found.') {
            errorMessage = 'Something went wrong. Please try again.';
          }
          
          const errorObj = {
            message: errorMessage,
            graphQLErrors: error.graphQLErrors,
            networkError: error.networkError,
            errorType: error.graphQLErrors?.[0]?.extensions?.classification || 'NETWORK_ERROR'
          };
          return throwError(errorObj);
        }
        
        return throwError(error);
      })
    );
  }

  /**
   * Execute a GraphQL query with token refresh
   * @param query GraphQL query document
   * @param variables Query variables
   * @returns Observable of query result
   */
  public queryWithAuth<T = any>(query: DocumentNode, variables?: any): Observable<T> {
    // The token refresh will be handled by the HTTP interceptor
    // No need to manually refresh the token here
    return this.query<T>(query, variables);
  }

  /**
   * Execute a GraphQL mutation with token refresh
   * @param mutation GraphQL mutation document
   * @param variables Mutation variables
   * @returns Observable of mutation result
   */
  public mutateWithAuth<T = any>(mutation: DocumentNode, variables?: any): Observable<T> {
    // The token refresh will be handled by the HTTP interceptor
    // No need to manually refresh the token here
    return this.mutate<T>(mutation, variables);
  }
}
